import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FieldSelectConfig } from './models/field-select-config.interface';
import { SelectOptionLoaderI } from './models/select-option-loader-i';
import { SelectOptionI } from './models/select-option-i';
import { AbstractField } from '../../models/field-configs/abstract-field';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { BaseAsyncValue, getObservableFromAsyncValueSafe } from '../../../../models/async-value';
import { RtlDetectorService } from '@ordersnproducts/modules/shared/services';
import { StatusMessageParameter } from '../../models/field-configs/field-config.interface';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { SearchTermChangeI } from './models/search-term-change.interface';
import { OrderFieldTestIds } from '@ordersnproducts/modules/order-field-test-ids-enum';

@Component({
  selector: 'form-select',
  styleUrls: ['form-select.component.scss'],
  templateUrl: 'form-select.component.html',
})
export class FormSelectComponent extends AbstractField implements OnInit, OnDestroy {
  // Main
  public group: UntypedFormGroup;
  public config: FieldSelectConfig;
  public fieldControl: UntypedFormControl;
  controlStatus$ = new BehaviorSubject('regular');
  controlStatusMessage$ = new BehaviorSubject('');
  temperatureMessage$ = new BehaviorSubject('');
  showExtraButtonValue: boolean;
  public orderFieldTestIds = OrderFieldTestIds;
  // View
  public rtl = false;
  public isRequired = false;
  public options: SelectOptionI[];
  public optionsCwc: any;
  public visible = true;
  public isLoading = false;
  public successLabel: string;
  public isDisabled = false;
  public value: any;
  public statusMessage: string;
  public statusMessageParameter: StatusMessageParameter;
  public customSelectedLabelPicker: (option: any) => string;

  private optionsLoader: SelectOptionLoaderI;
  private _ngDestroyed = new Subject();

  constructor(
    private injector: Injector,
    private rtlDetect: RtlDetectorService,
    private featureToggleService: FeatureToggleService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.resolveShowExtraButtonValue();
    this.resolveStatusValue();

    this.customSelectedLabelPicker = (option: any) => option.label + _safe(() => option.secondaryText, '');
    this.resolveSecondaryTextTransformValue();

    this.rtl = this.rtlDetect.activeLangRTL();
    this.options = this.config.options;

    this.initLoadingState();

    if (this.config.visible) {
      getObservableFromAsyncValueSafe(this.config.visible)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((fieldVisibility) => {
          this.visible = fieldVisibility;
          if (!this.visible) {
            this.config.containerClassNames.push('d-none');
          }
        });
    }

    if (this.config.containerClassNamesFn) {
      getObservableFromAsyncValueSafe(this.config.containerClassNamesFn)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((classNames) => (this.config.containerClassNames = classNames));
    }

    if (this.config.disabled) {
      getObservableFromAsyncValueSafe(this.config.disabled)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((fieldDisabled) => {
          this.isDisabled = fieldDisabled;
          if (fieldDisabled) {
            this.getControl().disable();
          } else {
            this.getControl().enable();
          }
        });
    }

    if (this.config.statusMessage) {
      getObservableFromAsyncValueSafe(this.config.statusMessage)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((statusMessage) => {
          this.statusMessage = statusMessage;
          this.controlStatusMessage$.next(statusMessage);
        });
    }

    if (this.config.statusMessageParameter) {
      getObservableFromAsyncValueSafe(this.config.statusMessageParameter)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((statusMessageParameter) => {
          this.statusMessageParameter = statusMessageParameter;
        });
    }

    if (this.config.hasOwnProperty('successLabel')) {
      this.successLabel = this.config.successLabel;
    }

    this.fieldControl = this.getControl() as UntypedFormControl;
    this.isRequired = this.hasRequiredField(this.fieldControl);

    this.fieldControl.statusChanges
      .pipe(
        map(() => {
          let tmpSelectedOption = null;
          if (this.fieldControl.value && this.options && this.options.length > 0) {
            tmpSelectedOption = this.options.find((option) => option.id === this.fieldControl.value);
          }
          const emptyOptions = _safe(() => !this.options.length, false);
          if (
            this.config.noRequiredErrorWhenEmpty &&
            this.fieldControl.hasError('required') &&
            (!this.options || emptyOptions) &&
            this.fieldControl.pristine
          ) {
            this.fieldControl.setErrors(null);
            this.fieldControl.markAsUntouched();
          } else if (tmpSelectedOption && tmpSelectedOption.status && tmpSelectedOption.status.length > 0) {
            this.controlStatus$.next(tmpSelectedOption.status);
          } else if (
            this.fieldControl.invalid &&
            this.fieldControl.hasError('required') &&
            !this.fieldControl.untouched
          ) {
            this.controlStatusMessage$.next('forms.required');
            this.controlStatus$.next('error');
          } else if (
            this.fieldControl.invalid &&
            this.fieldControl.hasError('error_no_message') &&
            !this.fieldControl.untouched
          ) {
            this.controlStatusMessage$.next(undefined);
            this.controlStatus$.next('error');
          } else if (this.successLabel && !this.fieldControl.invalid) {
            this.controlStatusMessage$.next(this.successLabel);
            this.controlStatus$.next('success');
          } else if (this.fieldControl.invalid && this.fieldControl.hasError('warning')) {
            this.controlStatusMessage$.next('');
            this.controlStatus$.next('warning');
          } else if (this.config.status === 'error' && !this.fieldControl.value) {
            this.controlStatusMessage$.next('forms.required');
            this.controlStatus$.next('error');
          } else if (this.config.statusMessage) {
            this.controlStatus$.next('regular');
            this.controlStatusMessage$.next(this.statusMessage);
          } else if (
            this.fieldControl.invalid &&
            this.fieldControl.hasError('required') &&
            this.featureToggleService.isFeatureEnabled(Features.IMPROVEMENTS_MODIFY_ORDER_RULES)
          ) {
            this.controlStatusMessage$.next('forms.required');
            this.controlStatus$.next('error');
          } else {
            this.controlStatusMessage$.next('');
            this.controlStatus$.next('regular');
          }
          this.isRequired = this.hasRequiredField(this.fieldControl);
        }),
      )
      .subscribe();

    // Custom loader so you don't have to create new component
    // each time you want to preload options from API
    const optionsLoader = this.config.optionsLoader;
    const optionsProvider = this.config.optionsProvider;

    if (optionsProvider !== undefined) {
      // If options provider defined. Use it as priority over options loader
      getObservableFromAsyncValueSafe(optionsProvider)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((options) => {
          const currentValue = this.group.get(this.config.name).value;
          this.options = this.getOptions(currentValue, this.options, options);
          if (this.config.undefinedOptionAvailable) {
            this.options.length && this.options.unshift({ id: undefined, name: undefined });
          }
          this.preselectDefaultOption();
        });
    } else if (optionsLoader !== undefined) {
      // Regular case
      this.optionsLoader = this.injector.get<SelectOptionLoaderI>(optionsLoader);
      this.optionsLoader
        .loadOptions(this.group, this.config)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((options) => {
          const currentValue = this.group.get(this.config.name).value;
          this.options = this.getOptions(currentValue, this.options, options);
          if (this.config.undefinedOptionAvailable) {
            this.options.length && this.options.unshift({ id: undefined, name: undefined });
          }
          this.preselectDefaultOption();
        });
    }

    if (this.config.temperatureMessage) {
      getObservableFromAsyncValueSafe(this.config.temperatureMessage)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((value) => this.temperatureMessage$.next(value));
    }

    this.fieldControl.valueChanges.subscribe(() => {
      this.value = this.fieldControl.value;
    });

    this.watchTouchedControl();
  }

  /**
   * Handles when user has selected value in a dropdown and performs search;
   * If a new option list doesn't contain a selected value we must show user which value is selected
   */
  private getOptions(controlValue: string, prev: SelectOptionI[], next: SelectOptionI[]): SelectOptionI[] {
    const optionsContainSelectedValue = controlValue && next.some((option) => option.id === controlValue);
    if (!controlValue || optionsContainSelectedValue) {
      return next;
    } else {
      let selectedOption = [];
      if (prev && prev.length > 0) {
        selectedOption = prev.filter((option) => option.id === controlValue);
      }
      return [...selectedOption, ...next];
    }
  }

  private initLoadingState() {
    if (this.config.loading) {
      getObservableFromAsyncValueSafe(this.config.loading)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((value) => (this.isLoading = value));
    }
  }

  private preselectDefaultOption(): void {
    let defaultValue: SelectOptionI;
    if (this.options && this.options.length > 0) {
      defaultValue = this.options.find((option) => option.default === true);
    }
    if (!!defaultValue && this.fieldControl && this.fieldControl.value === undefined) {
      this.fieldControl.setValue(defaultValue.id);
    }
  }

  public open() {
    if (this.config && this.config.onClickExtraButton) {
      if (this.config.onClickExtraButton.length > 0) {
        this.config.onClickExtraButton.forEach((pressButton) => {
          const pressButtonInstance = this.injector.get(pressButton);
          pressButtonInstance.onClickExtraButton(this.group, this.config);
        });
      } else {
        throw new Error('Method not implemented');
      }
    }
  }

  ngOnDestroy(): void {
    this._ngDestroyed.next();
    this._ngDestroyed.complete();
  }

  /**
   * Method that triggers when an icon in a cwc option is clicked by the user.
   *
   * Executes the onFieldPressTrashIcon methods of the classes added to the
   * config object for the select form field.
   * @param $event Parameter passed by the cwc event. The $event.detail.value equals the option's ID.
   */
  public ctaTrigger($event: CustomEvent): void {
    if (this.config && this.config.pressIconTrash) {
      if (this.config.pressIconTrash.length > 0) {
        this.config.pressIconTrash.forEach((pressIconTrash) => {
          const pressIconTrashInstance = this.injector.get(pressIconTrash);
          pressIconTrashInstance.onFieldPressTrashIcon($event);
        });
      } else {
        throw new Error('Method not implemented');
      }
    }
  }

  /**
   * Method that triggers when a user performs a filtering of options.
   *
   * Executes the searchChange methods of the classes added to the
   * config object for the select form field.
   * @param $event Parameter passed by the cwc event.
   */
  public filterChange({ detail }: CustomEvent): void {
    if (!this.config.hasOwnProperty('onFilterChange')) {
      return;
    }
    const onFilterChange = this.config.onFilterChange;
    const instance = this.injector.get<SearchTermChangeI>(onFilterChange);
    instance.searchChange(detail);
  }

  private resolveShowExtraButtonValue() {
    if (this.config.hasOwnProperty('showExtraButton')) {
      if (typeof this.config.showExtraButton === 'boolean') {
        this.showExtraButtonValue = this.config.showExtraButton;
      } else if (this.config.showExtraButton instanceof Observable) {
        this.config.showExtraButton.pipe(take(1)).subscribe((value) => {
          this.showExtraButtonValue = value;
        });
      } else if (this.config.showExtraButton instanceof BaseAsyncValue) {
        this.config.showExtraButton.getValue().subscribe((value) => {
          this.showExtraButtonValue = value;
        });
      }
    }
  }

  /**
   * Resolves the status value based on the provided input. Updates controlStatus$ observable.
   *
   * @param {}
   * @returns {}
   */
  private resolveStatusValue() {
    if (
      this.config.hasOwnProperty('statusAsync') &&
      (this.config.statusAsync instanceof Observable || this.config.statusAsync instanceof BaseAsyncValue)
    ) {
      getObservableFromAsyncValueSafe(this.config.statusAsync)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((value) => {
          if (value.length > 0) {
            this.controlStatus$.next(value);
          }
        });
    }
  }

  /**
   * Resolves the description based on the provided input. Updates customSelectedLabel .
   *
   * @param {}
   * @returns {}
   */
  private resolveSecondaryTextTransformValue() {
    if (this.config.hasOwnProperty('secondaryTextTransform')) {
      if (this.config.secondaryTextTransform instanceof BaseAsyncValue) {
        this.config.secondaryTextTransform.getValue().subscribe((value) => {
          this.customSelectedLabelPicker = value;
        });
      }
    }
  }

  /**
   * Resolves input errors if the status stays the same from the beginning and is marked as touched
   *
   * @returns {}
   */
  private watchTouchedControl(): void {
    const control = this.fieldControl;
    if (!control) {
      return;
    }

    new Observable<boolean>((observer) => {
      observer.next(control.touched);

      const originalMarkAsTouched = control.markAsTouched;
      control.markAsTouched = function (...args: any[]) {
        const result = originalMarkAsTouched.apply(control, args);
        observer.next(control.touched);
        return result;
      };

      return () => {
        control.markAsTouched = originalMarkAsTouched;
      };
    })
      .pipe(
        distinctUntilChanged(),
        filter(() => this.featureToggleService.isFeatureEnabled(Features.FORM_ERROR_HANDLING_ON_TOUCHED)),
        takeUntil(this._ngDestroyed),
      )
      .subscribe((touched) => {
        if (touched && control.invalid && control.hasError('required')) {
          this.controlStatusMessage$.next('forms.required');
          this.controlStatus$.next('error');
        }
      });
  }
}
